import { readonly, ref } from "vue";

const currentVersion = process.env.VUE_APP_BUILD_VERSION;
const updateAvailable = ref(false);
const checkInterval = 60 * 1000;
const requestTimeout = 10 * 1000;
let pendingCheck: Promise<void> | undefined;

export const appUpdateAvailable = readonly(updateAvailable);

export function markAppUpdateAvailable(): void {
  updateAvailable.value = true;
}

export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /Loading (?:CSS )?chunk .+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      error.message
    )
  );
}

export function checkForAppUpdate(): Promise<void> {
  if (!currentVersion) return Promise.resolve();
  if (pendingCheck) return pendingCheck;

  pendingCheck = (async () => {
    const baseUrl = new URL(
      process.env.BASE_URL || "/",
      window.location.origin
    );
    const url = new URL("version.json", baseUrl);
    url.searchParams.set("_", Date.now().toString());
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), requestTimeout);

    try {
      const response = await fetch(url.toString(), {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!response.ok) return;

      const data = await response.json();
      if (
        typeof data?.version === "string" &&
        data.version.trim() &&
        data.version !== currentVersion
      ) {
        markAppUpdateAvailable();
      }
    } catch {
      // A failed check must not interrupt work or imply a different version.
    } finally {
      window.clearTimeout(timeout);
    }
  })().finally(() => {
    pendingCheck = undefined;
  });

  return pendingCheck;
}

export function startAppVersionCheck(): () => void {
  if (!currentVersion) return () => undefined;

  const checkWhenVisible = () => {
    if (document.visibilityState === "visible") void checkForAppUpdate();
  };
  void checkForAppUpdate();
  const interval = window.setInterval(checkWhenVisible, checkInterval);
  window.addEventListener("focus", checkWhenVisible);
  window.addEventListener("online", checkWhenVisible);
  document.addEventListener("visibilitychange", checkWhenVisible);

  return () => {
    window.clearInterval(interval);
    window.removeEventListener("focus", checkWhenVisible);
    window.removeEventListener("online", checkWhenVisible);
    document.removeEventListener("visibilitychange", checkWhenVisible);
  };
}
